<template>
    <div>
        <div class="is-clearfix">
            <div class="chart-title is-pulled-left">
                <h1 class="title">{{title}}</h1>
                <h2 class="subtitle">{{subtitle}}</h2>
            </div>

            <div class="is-pulled-right level">
                <b-select class="is-pulled-left" size="is-small" v-model="selectedBrand" v-if="availableBrands.length">
                    <option v-for="brand in availableBrands" :value="brand" :key="brand">
                        {{ brand }}
                    </option>
                </b-select>

                <b-select class="is-pulled-left" size="is-small" v-model="selectedPillar" v-if="availableBrands.length">
                    <option v-for="pillar in availablePillars" :value="pillar" :key="pillar.key">
                        {{ pillar.name }}
                    </option>
                </b-select>

                <button class="is-pulled-right button is-small" @click="exportPPTX" :disabled="isExporting || !availableBrands.length">
                    <b-icon icon="file-powerpoint"
                            size="is-small"
                            pack="far">
                    </b-icon>
                    <span>Export</span>
                </button>
            </div>

        </div>
        <hr>
        <chart class="chart-content" :chartData="chartData"/>
        <chart-legend :legends="chartData.settings.series" />
        <div style="clear: both"></div>
    </div>
</template>

<script>
import Chart from '@/components/charts/Chart.vue';
import ChartLegend from '@/components/charts/ChartLegend.vue';

import _ from 'lodash';

import { exporterService } from '@/services/exporter-service';
import { brandMetricDetailProgressRequestor } from '@/requestors/brand-metric-detail-progress-requestor.js';
import { chartTypes } from '@/components/charts/common/chartTypes';
import { chartLabelGeneratorService } from '@/services/chart-label-generator-service';
import { progressChartHelperService } from '@/services/progress-chart-helper-service';
import DefaultMetricWeightService from '@/services/default-metric-weight-service';
import colourService from '@/services/colour-service';

export default {
    name: 'BrandMetricDetailProgress',
    components: {
        Chart,
        ChartLegend
    },
    props: {
        project: {
            type: Object,
            required: true
        }
    },
    data () {
        return {
            title: 'Brand Metric Trend within Pillar',
            selectedDateRangeText: '',
            chartData: {
                chartType: chartTypes.ORDINALLINE,
                settings: {
                    xAxis: {
                        label: 'Date',
                        noOverlap: true
                    },
                    yAxis: {
                        minValue: 0,
                        label: 'Score',
                        ticks: 5
                    },
                    series: []
                },
                meta: {
                    metric: ['date', 'score'],
                    series: 'series'
                },
                data: []
            },
            availableBrands: [],
            availablePillars: [],
            selectedPillar: '',
            selectedBrand: '',
            isExporting: false
        };
    },
    watch: {
        'project.metrics': function () {
            this.chartData.data = generateDataMetrics(this.project.metrics, this.selectedBrand, this.chartData.settings.series);
            const latestMetric = _.maxBy(this.project.metrics, m => m.day);
            const earliestMetric = _.minBy(this.project.metrics, m => m.day);
            if (earliestMetric && latestMetric) {
                let strPattern = chartLabelGeneratorService.generateProgressChartLabelPattern(this.project.meta.period, this.project.meta.daySmoothing);
                this.selectedDateRangeText = chartLabelGeneratorService.formatString(strPattern, earliestMetric.periodStartDate, latestMetric.periodEndDate, this.project.meta.daySmoothing);
            }
            this.chartData.settings.xAxis.label = progressChartHelperService.buildXAxisTitle(this.project.meta.period, this.project.meta.daySmoothing);
        },
        'project.settings.columns': {
            handler: function () {
                this.availableBrands = _(this.project.settings.columns)
                    .filter(c => c.enabled)
                    .map(c => c.name)
                    .uniq()
                    .value();

                if (!this.selectedBrand || !_.find(this.availableBrands, b => b === this.selectedBrand)) {
                    this.selectedBrand = this.availableBrands[0];
                }
            },
            deep: true
        },
        selectedBrand: function () {
            this.chartData.data = generateDataMetrics(this.project.metrics, this.selectedBrand, this.chartData.settings.series);
        },
        selectedPillar: function () {
            this.chartData.settings.series = generateChartSeries(this.selectedPillar.metrics);
            this.chartData.data = generateDataMetrics(this.project.metrics, this.selectedBrand, this.chartData.settings.series);
        }
    },
    methods: {
        async exportPPTX () {
            this.isExporting = true;
            let pptxSubtitle = this.subtitle;
            let pptxRequest = brandMetricDetailProgressRequestor.generatePPTXRequest(this.chartData.data, this.title, pptxSubtitle, this.chartData.settings);

            try {
                await exporterService.exportPPTX(pptxRequest);
            } catch (error) {
                this.$buefy.toast.open({
                    message: 'Failed to export Brand Metric Detail Trend Chart',
                    type: 'is-danger'
                });
            } finally {
                this.isExporting = false;
            }
        }
    },
    mounted () {
        let chartDetail = {
            getSlideRequest: () => {
                let pptxSubtitle = this.subtitle;
                return brandMetricDetailProgressRequestor.getSlideRequest(this.chartData.data, this.title, pptxSubtitle, this.chartData.settings);
            }
        };
        this.$emit('on-chart-mount-complete', chartDetail);
    },
    created () {
        // TODO: Consider whether metrics can be retrieved without weight information
        DefaultMetricWeightService.getDefaultMetricWeights()
            .then(response => {
                let groupedMetrics = _.groupBy(response.data, 'axis');

                this.availablePillars.push(
                    {
                        name: 'Conversation',
                        key: 'x',
                        metrics: groupedMetrics.X
                    },
                    {
                        name: 'Quality',
                        key: 'y',
                        metrics: groupedMetrics.Y
                    },
                    {
                        name: 'Community',
                        key: 'z',
                        metrics: groupedMetrics.Z
                    }
                );
                this.selectedPillar = this.availablePillars[0];
            })
            .catch(() => {
                console.log('Failed to get available metrics');
            });
    },
    computed: {
        subtitle () {
            let selectedPillarText = this.selectedPillar ? this.selectedPillar.name + ': ' : '';
            return `${this.selectedBrand} - ${selectedPillarText}${this.selectedDateRangeText}`;
        }
    }
};

const generateDataMetrics = (projectMetrics, selectedBrand, series) => {
    const dataMetrics = [];

    projectMetrics.forEach(el => {
        if (el.brandName === selectedBrand) {
            series.forEach(s => {
                dataMetrics.push({
                    series: s.id,
                    brand: el.brandName,
                    date: el.periodName,
                    score: el.normedBrandMetrics[s.id]
                });
            });
        }
    });

    return dataMetrics;
};

const generateChartSeries = (selectedPillar) => {
    return _(selectedPillar)
        .map((m, index) => ({
            id: m.sysMetric,
            name: m.platform + ' - ' + m.metric,
            colour: colourService.getColours[index],
            visible: true
        }))
        .value();
};

</script>

<style lang="scss" scoped>
    .chart-content {
        width: 100%;
        height: 300px;
    }
    .title {
        font-size: 16px;
    }
    .subtitle {
        font-size: 14px;
        margin-bottom: 5px;
    }
    hr {
        margin: 5px;
    }
</style>
